/**
 * News labels
 * Dynamic mapping from JSON based on current language
 * Uses getLanguage() to determine which locale to use
 */

import svNews from '../locales/sv/news.json';
import enNews from '../locales/en/news.json';
import { getLanguage, type Language } from '@/utils/languageUtils';

/**
 * Helper function to get the correct locale based on language
 */
const getNewsLocale = (lang?: Language) => {
  const currentLang = lang || getLanguage();
  return currentLang === 'en' ? enNews : svNews;
};

/**
 * Target User Type labels - dynamically uses current language
 */
export const targetUserTypeLabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang);
  return {
    all: locale.targetUserType.all,
    dealers: locale.targetUserType.dealers,
    business: locale.targetUserType.business,
    individuals: locale.targetUserType.individuals,
    internally: locale.targetUserType.internally,
  };
};

/**
 * News Status labels - dynamically uses current language
 */
export const newsStatusLabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang);
  return {
    published: locale.status.published,
    hidden: locale.status.hidden,
    visible: locale.status.published, // Alias for published
    draft: locale.status.hidden, // Alias for hidden
  };
};

/**
 * News Actions labels - dynamically uses current language
 */
export const newsActionLabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang);
  return {
    publish: locale.actions.publish,
    unpublish: locale.actions.unpublish,
  };
};

/**
 * News Messages labels - dynamically uses current language
 */
export const newsMessageLabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang);
  return {
    published: locale.messages.published,
    hidden: locale.messages.hidden,
    deleted: locale.messages.deleted,
    updated: locale.messages.updated,
    created: locale.messages.created,
    updatedSuccess: locale.messages.updatedSuccess,
    deletedSuccess: locale.messages.deletedSuccess,
    imageUploaded: locale.messages.imageUploaded,
    imageRemoved: locale.messages.imageRemoved,
    imageUploadError: locale.messages.imageUploadError,
    imageRemoveError: locale.messages.imageRemoveError,
    updateError: locale.messages.updateError,
    createError: locale.messages.createError,
    deleteError: locale.messages.deleteError,
    toggleVisibilityError: locale.messages.toggleVisibilityError,
    removed: locale.messages.removed,
  };
};

/**
 * News UI Labels - dynamically uses current language
 */
export const newsUILabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang);
  return {
    published: locale.labels.published,
    created: locale.labels.created,
  };
};

/**
 * News Confirmations labels - dynamically uses current language
 */
export const newsConfirmationLabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang);
  return {
    deleteNews: locale.confirmations.deleteNews,
    togglePublish: locale.confirmations.togglePublish,
  };
};

/**
 * News Error labels - dynamically uses current language
 */
export const newsErrorLabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang);
  return {
    selectRecipient: locale.errors.selectRecipient,
    mustBeLoggedIn: locale.errors.mustBeLoggedIn,
    mustBeLoggedInToCreate: locale.errors.mustBeLoggedInToCreate,
    titleAndContentRequired: locale.errors.titleAndContentRequired,
  };
};

/**
 * News UI Text labels - dynamically uses current language
 */
export const newsUITextLabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang);
  const ui = locale.ui as Record<string, string>;
  return {
    loadingNews: locale.ui.loadingNews,
    noNewsHistory: locale.ui.noNewsHistory,
    noNewsAvailable: locale.ui.noNewsAvailable,
    latestNews: locale.ui.latestNews,
    clickToReadMore: locale.ui.clickToReadMore,
    editNews: locale.ui.editNews,
    createNews: locale.ui.createNews,
    createNewsTitle: locale.ui.createNewsTitle,
    createNewsDescription: locale.ui.createNewsDescription,
    publishNews: locale.ui.publishNews,
    news: locale.ui.news,
    manageNewsDescription: locale.ui.manageNewsDescription,
    labels: locale.ui.labels,
    labelsPlaceholder: locale.ui.labelsPlaceholder,
    uploading: locale.ui.uploading,
    removing: locale.ui.removing,
    changeImage: locale.ui.changeImage,
    addImage: locale.ui.addImage,
    removeImage: locale.ui.removeImage,
    viewImage: locale.ui.viewImage,
    minReading: locale.ui.minReading,
    recipient: ui.recipient,
    headline: ui.headline,
    headlinePlaceholder: ui.headlinePlaceholder,
    contentPlaceholder: ui.contentPlaceholder,
    uploadImage: ui.uploadImage,
    searchUser: ui.searchUser,
    searchUserPlaceholder: ui.searchUserPlaceholder,
    searchResultsCount: ui.searchResultsCount,
    selectedUsersCount: ui.selectedUsersCount,
    addUser: ui.addUser,
    selected: ui.selected,
    content: ui.content,
    imageSelected: ui.imageSelected,
    invalidFileFormat: ui.invalidFileFormat,
    imageTooLarge: ui.imageTooLarge,
    publishing: ui.publishing,
    saving: ui.saving,
    enterSearchTerm: ui.enterSearchTerm,
    searchFailed: ui.searchFailed,
    searchButton: ui.searchButton,
    clearSearch: ui.clearSearch,
    searchUserPlaceholderExtended: ui.searchUserPlaceholderExtended,
    recipientPreviewFailed: ui.recipientPreviewFailed,
    recipientPreviewEmpty: ui.recipientPreviewEmpty,
    recipientPreviewCount: ui.recipientPreviewCount,
    selectRecipient: locale.errors.selectRecipient,
    recipientPoolTitle: ui.recipientPoolTitle,
    recipientPoolHint: ui.recipientPoolHint,
    recipientPoolPageSelected: ui.recipientPoolPageSelected,
    done: ui.done,
    addSpecificUser: ui.addSpecificUser,
    addSpecificUserTitle: ui.addSpecificUserTitle,
    addSpecificUserDescription: ui.addSpecificUserDescription,
    extraRecipientsTitle: ui.extraRecipientsTitle,
    extraRecipientBadge: ui.extraRecipientBadge,
    userAlreadyInList: ui.userAlreadyInList,
    userAddedToRecipients: ui.userAddedToRecipients,
    removeUser: ui.removeUser,
    customEmailsTitle: ui.customEmailsTitle,
    customEmailsHint: ui.customEmailsHint,
    customEmailPlaceholder: ui.customEmailPlaceholder,
    customEmailAdd: ui.customEmailAdd,
    customEmailBadge: ui.customEmailBadge,
    customEmailInvalid: ui.customEmailInvalid,
    customEmailDuplicate: ui.customEmailDuplicate,
    customEmailCsvTitle: ui.customEmailCsvTitle,
    customEmailCsvFormat: ui.customEmailCsvFormat,
    customEmailCsvUpload: ui.customEmailCsvUpload,
    customEmailCsvInvalidType: ui.customEmailCsvInvalidType,
    customEmailCsvEmpty: ui.customEmailCsvEmpty,
    customEmailCsvNoNew: ui.customEmailCsvNoNew,
    customEmailCsvImported: ui.customEmailCsvImported,
    customEmailCsvReadFailed: ui.customEmailCsvReadFailed,
    leadsRecipient: ui.leadsRecipient,
    leadRecipientBadge: ui.leadRecipientBadge,
    leadsRecipientsTitle: ui.leadsRecipientsTitle,
    leadsEditSelection: ui.leadsEditSelection,
    leadsDialogTitle: ui.leadsDialogTitle,
    leadsDialogDescription: ui.leadsDialogDescription,
    leadsDialogEmpty: ui.leadsDialogEmpty,
    leadsDialogCancel: ui.leadsDialogCancel,
    leadsDialogConfirm: ui.leadsDialogConfirm,
    leadsStatusFilterHint: ui.leadsStatusFilterHint,
    leadsSearchPlaceholder: ui.leadsSearchPlaceholder,
    leadsSelectedCount: ui.leadsSelectedCount,
    leadsSelectedRecipients: ui.leadsSelectedRecipients,
    leadsSelectedInFilter: ui.leadsSelectedInFilter,
    leadsSelectAllFiltered: ui.leadsSelectAllFiltered,
    leadsDeselectAllFiltered: ui.leadsDeselectAllFiltered,
    leadsSharedEmail: ui.leadsSharedEmail,
    leadsRegNumber: ui.leadsRegNumber,
    leadsReceivedAt: ui.leadsReceivedAt,
  };
};

/**
 * Marketing campaign labels (for admin marknadsföring tab)
 */
export const getMarketingLabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang) as { marketing?: Record<string, string> };
  const m = locale.marketing || {};
  return {
    tab: m.tab ?? "Marknadsföring",
    tabNews: m.tabNews ?? "Nyheter",
    tabInformation: m.tabInformation ?? "Informationsutskick",
    title: m.title ?? "Skicka marknadsföring",
    description: m.description ?? "",
    recipientType: m.recipientType ?? "Mottagartyp",
    recipientMode: m.recipientMode ?? "Mottagare",
    recipientTypes: m.recipientTypes ?? "Mottagartyp",
    specificUsers: m.specificUsers ?? "Specifika användare",
    selectAtLeastOneUser: m.selectAtLeastOneUser ?? "Välj minst en användare.",
    selectAtLeastOneRecipient:
      m.selectAtLeastOneRecipient ?? "Välj minst en mottagartyp eller specifik användare.",
    recipientCombinedHint:
      m.recipientCombinedHint ??
      "Du kan kombinera mottagartyp och specifika användare. Varje person får högst ett mejl.",
    signature: m.signature ?? "Signatur (valfritt)",
    signaturePlaceholder:
      m.signaturePlaceholder ?? "Med vänliga hälsningar,\nBILENIA-teamet",
    signatureHint: m.signatureHint ?? "Rad 1 = hälsningsfras, rad 2 = avsändarnamn.",
    bold: m.bold ?? "Fet",
    italic: m.italic ?? "Kursiv",
    h1: m.h1 ?? "Rubrik",
    h2: m.h2 ?? "Underrubrik",
    bullets: m.bullets ?? "Punktlista",
    formattingHint:
      m.formattingHint ??
      "Formatering: **fet**, *kursiv*, # rubrik, ## underrubrik (en per rad).",
    imageUpload: m.imageUpload ?? "Bild (valfritt)",
    removeImage: m.removeImage ?? "Ta bort",
    imageUploadInvalidType: m.imageUploadInvalidType ?? "Välj en bildfil (PNG/JPG/WebP).",
    imageUploadFailed: m.imageUploadFailed ?? "Kunde inte ladda upp bilden.",
    preview: m.preview ?? "Förhandsgranska",
    previewTitle: m.previewTitle ?? "Förhandsgranskning",
    previewSubject: m.previewSubject ?? "Ämne",
    previewFailed: m.previewFailed ?? "Kunde inte skapa förhandsgranskning.",
    recipientDealer: m.recipientDealer ?? "Handlare",
    sendType: m.sendType ?? "Utskickstyp",
    sendTypeGeneral: m.sendTypeGeneral ?? "Generellt",
    sendTypeListings: m.sendTypeListings ?? "Annonser",
    listingsTitle: m.listingsTitle ?? "Senaste annonser",
    listingsDescription: m.listingsDescription ?? "",
    selectListings: m.selectListings ?? "Välj annonser",
    selectAtLeastOne: m.selectAtLeastOne ?? "Välj minst en annons",
    sendButton: m.sendButton ?? "Skicka utskick",
    emailSignoffSection: m.emailSignoffSection ?? "Signatur i mejlet (valfritt)",
    signoffGreeting: m.signoffGreeting ?? "Hälsningsfras",
    signoffGreetingPlaceholder: m.signoffGreetingPlaceholder ?? "Med vänliga hälsningar,",
    signoffTeamName: m.signoffTeamName ?? "Avsändarnamn",
    signoffTeamNamePlaceholder: m.signoffTeamNamePlaceholder ?? "BILENIA-teamet",
    sending: m.sending ?? "Skickar...",
    generalTitle: m.generalTitle ?? "Rubrik (valfritt)",
    generalTitlePlaceholder: m.generalTitlePlaceholder ?? "",
    generalContent: m.generalContent ?? "Innehåll (valfritt)",
    generalContentPlaceholder: m.generalContentPlaceholder ?? "",
    generalDescription: m.generalDescription ?? "",
    successCount: m.successCount ?? "Utskick skickat till {count} mottagare.",
    errorSend: m.errorSend ?? "Kunde inte skicka utskick.",
    loadingListings: m.loadingListings ?? "Laddar annonser...",
    selectAllListings: m.selectAllListings ?? "Välj alla",
    deselectAllListings: m.deselectAllListings ?? "Avmarkera alla",
    listingTypeAuction: m.listingTypeAuction ?? "Auktion",
    listingTypeDirectPurchase: m.listingTypeDirectPurchase ?? "Direktköp",
    mileageUnit: m.mileageUnit ?? " mil",
    noListingsToShow: m.noListingsToShow ?? "Inga aktiva eller reserverade annonser att visa.",
    specificUsersHint: m.specificUsersHint ?? "",
    previewRecipients: m.previewRecipients ?? "Visa mottagare",
    previewRecipientsTitle: m.previewRecipientsTitle ?? "Mottagare",
    previewRecipientsDescription: m.previewRecipientsDescription ?? "",
    addSpecificUser: m.addSpecificUser ?? "Lägg till specifik användare",
    addSpecificUserTitle: m.addSpecificUserTitle ?? "Lägg till specifik användare",
    addSpecificUserDescription: m.addSpecificUserDescription ?? "",
  };
};

/**
 * Information campaign labels (for mandatory informational emails)
 */
export const getInformationLabels = (lang?: Language): Record<string, string> => {
  const locale = getNewsLocale(lang) as {
    information?: Record<string, string>;
  };
  const i = locale.information || {};
  return {
    recipientMode: i.recipientMode ?? "Mottagare",
    recipientTypes: i.recipientTypes ?? "Mottagartyp",
    specificUsers: i.specificUsers ?? "Specifika användare",
    selectAtLeastOneUser: i.selectAtLeastOneUser ?? "Välj minst en användare.",
    selectAtLeastOneRecipient:
      i.selectAtLeastOneRecipient ?? "Välj minst en mottagartyp eller specifik användare.",
    recipientCombinedHint:
      i.recipientCombinedHint ??
      "Du kan kombinera mottagartyp och specifika användare. Varje person får högst ett mejl.",
    title: i.title ?? "Informationsutskick",
    description:
      i.description ??
      "Skicka ett viktigt informationsbrev som går ut även till användare som har stängt av marknadsföring.",
    warningTitle: i.warningTitle ?? "Viktigt: detta går ut till ALLA",
    warningBody:
      i.warningBody ??
      "Syftet är akuta saker eller information som måste nå alla användare i vald mottagartyp. Var försiktig med detta utskick.",
    generalTitle: i.generalTitle ?? "Rubrik",
    generalTitlePlaceholder: i.generalTitlePlaceholder ?? "T.ex. Viktig information",
    generalContent: i.generalContent ?? "Innehåll",
    generalContentPlaceholder:
      i.generalContentPlaceholder ?? "Skriv ditt informationsmeddelande...",
    generalDescription:
      i.generalDescription ??
      "Detta utskick är inte reklam och innehåller ingen möjlighet att stänga av i inställningarna.",
    sendButton: i.sendButton ?? "Skicka informationsutskick",
    formattingHint:
      i.formattingHint ??
      "Formatering: **fet**, *kursiv*, # rubrik, ## underrubrik (en per rad).",
    bold: i.bold ?? "Fet",
    italic: i.italic ?? "Kursiv",
    h1: i.h1 ?? "Rubrik",
    h2: i.h2 ?? "Underrubrik",
    bullets: i.bullets ?? "Punktlista",
    imageUpload: i.imageUpload ?? "Bild (valfritt)",
    removeImage: i.removeImage ?? "Ta bort",
    imageUploadInvalidType: i.imageUploadInvalidType ?? "Välj en bildfil (PNG/JPG/WebP).",
    imageUploadFailed: i.imageUploadFailed ?? "Kunde inte ladda upp bilden.",
    signature: i.signature ?? "Signatur (valfritt)",
    signaturePlaceholder:
      i.signaturePlaceholder ?? "Med vänliga hälsningar,\nBILENIA-teamet",
    signatureHint:
      i.signatureHint ?? "Rad 1 = hälsningsfras, rad 2 = avsändarnamn.",
    preview: i.preview ?? "Förhandsgranska",
    previewTitle: i.previewTitle ?? "Förhandsgranskning",
    previewSubject: i.previewSubject ?? "Ämne",
    previewFailed: i.previewFailed ?? "Kunde inte skapa förhandsgranskning.",
    sending: i.sending ?? "Skickar...",
    successCount: i.successCount ?? "Utskick skickat till {count} mottagare.",
    errorSend: i.errorSend ?? "Kunde inte skicka utskick.",
    specificUsersHint: i.specificUsersHint ?? "",
    previewRecipients: i.previewRecipients ?? "Visa mottagare",
    previewRecipientsTitle: i.previewRecipientsTitle ?? "Mottagare",
    previewRecipientsDescription: i.previewRecipientsDescription ?? "",
    addSpecificUser: i.addSpecificUser ?? "Lägg till specifik användare",
    addSpecificUserTitle: i.addSpecificUserTitle ?? "Lägg till specifik användare",
    addSpecificUserDescription: i.addSpecificUserDescription ?? "",
  };
};

